import { CONFIG } from '../data/config'
import { DIAS, plata } from './format'
import type { DatosContacto, ItemPedido, Pedido, Producto, ProximaEntrega } from '../types/pedido'

export type Carrito = Record<string, number>

export function itemsDelCarrito(carrito: Carrito, productos: Producto[] = CONFIG.productos) {
  return productos
    .filter((p) => (carrito[p.id] ?? 0) > 0)
    .map((p) => ({ ...p, cant: carrito[p.id], sub: p.precio * carrito[p.id] }))
}

export function totalCarrito(carrito: Carrito, productos: Producto[] = CONFIG.productos): number {
  return itemsDelCarrito(carrito, productos).reduce((a, i) => a + i.sub, 0)
}

export function unidadesCarrito(carrito: Carrito): number {
  return Object.values(carrito).reduce((a, n) => a + n, 0)
}

interface ArmarArgs {
  carrito: Carrito
  datos: DatosContacto
  barrio: string
  entrega: ProximaEntrega | null
  rutaFallback: string
}

export function armarPedido({ carrito, datos, barrio, entrega, rutaFallback }: ArmarArgs): Pedido {
  const its = itemsDelCarrito(carrito)
  const items: ItemPedido[] = its.map((i) => ({
    id: i.id,
    nombre: i.nombre,
    cant: i.cant,
    precio: i.precio,
    sub: i.sub,
  }))
  const direccion = [datos.calle.trim(), datos.piso.trim()].filter(Boolean).join(', ')

  return {
    id: 'P' + Date.now().toString(36).slice(-5).toUpperCase(),
    creado: new Date().toISOString(),
    cliente: datos.nombre.trim(),
    telefono: datos.tel.trim(),
    barrio,
    ruta: entrega ? entrega.ruta : rutaFallback,
    diaReparto: entrega ? DIAS[entrega.dia] : 'a coordinar',
    fechaEntrega: entrega ? entrega.fecha.toISOString().slice(0, 10) : '',
    direccion,
    entreCalles: datos.entre.trim(),
    direccionCompleta: `${datos.calle.trim()}, ${barrio === '(fuera de zona)' ? '' : barrio + ', '}Buenos Aires, Argentina`,
    items,
    unidades: items.reduce((a, i) => a + i.cant, 0),
    total: items.reduce((a, i) => a + i.sub, 0),
    pago: datos.pago,
    notas: datos.notas.trim(),
  }
}

export function textoWhatsApp(p: Pedido): string {
  const L: string[] = []
  L.push(`*NUEVO PEDIDO ${p.id}*`)
  L.push('')
  L.push(`*${p.barrio}*  ·  entrega ${p.diaReparto}${p.fechaEntrega ? ' ' + p.fechaEntrega.split('-').reverse().join('/') : ''}`)
  L.push(p.direccion)
  if (p.entreCalles) L.push(`entre ${p.entreCalles}`)
  L.push('')
  p.items.forEach((i) => L.push(`${i.cant} × ${i.nombre} — ${plata(i.sub)}`))
  L.push('')
  L.push(`*TOTAL ${plata(p.total)}*  (${p.pago})`)
  L.push('')
  L.push(`${p.cliente} · ${p.telefono}`)
  if (p.notas) L.push(`Nota: ${p.notas}`)
  return L.join('\n')
}

export function linkWhatsApp(texto: string): string {
  return `https://wa.me/${CONFIG.whatsapp}?text=${encodeURIComponent(texto)}`
}

/** Sin header Content-Type a propósito: evita el preflight CORS de Apps Script. */
export async function registrarPedido(p: Pedido): Promise<void> {
  if (!CONFIG.apiUrl) return
  try {
    await fetch(CONFIG.apiUrl, { method: 'POST', body: JSON.stringify(p) })
  } catch (err) {
    console.warn('No se pudo registrar el pedido en el servidor:', err)
  }
}
